import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import moment from 'moment';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { undoCalendarAction } from '../../ducks/calendarActions';
import useExpiryTick from '../../hooks/useExpiryTick';
import getUndoActionLabel from '../../utils/getUndoActionLabel';

const IconWrapperStyle = styled('div')`
  cursor: default;
  transition: all 250ms ease-in-out;
  :hover {
    opacity: 0.7;
  }
`;

const BorderedIconWrapper = styled(IconWrapperStyle)`
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FadingIcon = styled('div')`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconContainer = styled('div')`
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UndoButton = styled('div')`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.link};
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 250ms ease-in-out;
  user-select: none;
  :hover {
    opacity: 0.7;
  }
`;

const CloudUploadIcon = () => {
  const dispatch = useDispatch();
  const { buildUndo } = useFlags();

  // Check if any saving or updating operations are in progress
  /** Any save in flight, from the per-item map. The single isCalendarItemSaving
   * flag was cleared by the first of several overlapping saves to finish. */
  const isCalendarItemSaving = useSelector(
    (state) => Object.keys(state.calendar.ui.savingAssignedIds).length > 0,
  );
  const isCalendarItemUpdating = useSelector(
    (state) => state.calendar.ui.isCalendarItemUpdating,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItems = useSelector(
    (state) => state.calendar.data.calendarItems,
  );
  const calendarAction = useSelector(
    (state) => state.calendar.data.calendarAction,
  );
  const isUndoingCalendarAction = useSelector(
    (state) => state.calendar.data.isUndoingCalendarAction,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { colors } = useTheme();

  // Generate unique ID for tooltip
  const uploadTooltipId = 'upload-tooltip';
  const undoTooltipId = 'undo-tooltip';

  // Get current day's calendar items using selectedDate
  // Handle both Moment objects and JavaScript Date objects
  let selectedDateString = null;
  if (selectedDate) {
    if (moment.isMoment(selectedDate)) {
      selectedDateString = selectedDate.format('YYYY-MM-DD');
    } else {
      // Convert JavaScript Date object to Moment and format
      selectedDateString = moment(selectedDate).format('YYYY-MM-DD');
    }
  }

  const currentDayItemObject = calendarItems
    ? calendarItems[selectedDateString]
    : null;

  // State to track whether we should show the loading spinner
  const [showSpinner, setShowSpinner] = useState(false);
  const [currentDayLastUpdated, setCurrentDayLastUpdated] = useState(null);

  // Base loading state from Redux
  const isLoading = isCalendarItemSaving || isCalendarItemUpdating;

  // Effect to set the last updated timestamp when data loads
  useEffect(() => {
    if (currentDayItemObject?.lastUpdatedTimestamp) {
      const timestamp = currentDayItemObject.lastUpdatedTimestamp;
      // Handle both seconds (10 digits) and milliseconds (13 digits) timestamps
      // If timestamp is in seconds (10 digits), convert to milliseconds
      const timestampInMilliseconds = timestamp.toString().length === 10
        ? timestamp * 1000
        : timestamp;
      setCurrentDayLastUpdated(timestampInMilliseconds);
    } else {
      setCurrentDayLastUpdated(null);
    }
  }, [currentDayItemObject]);

  // Effect to handle the delayed spinner visibility
  useEffect(() => {
    let timeoutId;

    // If loading starts, show spinner immediately
    if (isLoading) {
      setShowSpinner(true);
    } else if (showSpinner) {
      // If loading ends, set a timeout to hide the spinner after 1 second
      timeoutId = setTimeout(() => {
        setShowSpinner(false);
      }, 1000); // Keep spinner for 1 second after loading finishes
    }

    // Clean up timeout if component unmounts or loading state changes again
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, showSpinner]);

  const handleUndo = () => {
    if (currentUser?.accountCode) {
      dispatch(undoCalendarAction(currentUser.accountCode));
    }
  };

  const handleUndoKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleUndo();
    }
  };

  // Endpoints not yet migrated to the richer calendarAction shape won't send
  // expiresAt/canBeUndone - treat those as still-eligible (the pre-migration
  // behavior) rather than hiding the button for lack of information.
  // Undo isn't supported for free-form Programs on the backend yet. This is
  // "is there a target at all" - separate from expiry below - because those
  // two reasons get different treatment: no target means the backend has
  // nothing left to undo (button disappears), while an expired target still
  // greys out visibly rather than vanishing.
  const hasUndoTarget = buildUndo
    && programType !== 'free-form-program'
    && Boolean(calendarAction?.id)
    && calendarAction.canBeUndone !== false;

  const isExpired = hasUndoTarget
    && Boolean(calendarAction.expiresAt)
    && moment(calendarAction.expiresAt).isBefore(moment());

  // Re-renders exactly when calendarAction.expiresAt passes, so isExpired
  // above flips live instead of waiting on some unrelated re-render.
  useExpiryTick([calendarAction?.expiresAt]);

  const isDisabled = isUndoingCalendarAction || isExpired;

  // Excel-style hover hint naming the action type ("Undo Clear"). Falls back
  // to a plain "Undo" for legacy targets that carry no actionType.
  const undoTooltipText = getUndoActionLabel(calendarAction?.actionType);

  return (
    <>
      {hasUndoTarget && (
        <UndoButton
          role='button'
          tabIndex={0}
          onClick={isDisabled ? undefined : handleUndo}
          onKeyDown={isDisabled ? undefined : handleUndoKeyDown}
          data-tooltip-id={undoTooltipId}
          data-tooltip-content={undoTooltipText}
          style={
            isDisabled
              ? { opacity: 0.5, cursor: 'default' }
              : undefined
          }
        >
          <IcomoonReact
            iconSet={iconSet}
            size={14}
            icon='undo'
            color={colors.link}
          />
          UNDO
        </UndoButton>
      )}
      {hasUndoTarget && (
        <ReactTooltip
          id={undoTooltipId}
          place='bottom'
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
          }}
        />
      )}
      <BorderedIconWrapper
        data-tooltip-id={currentDayLastUpdated ? uploadTooltipId : undefined}
      >
        <IconContainer>
          <FadingIcon isVisible={showSpinner}>
            <Spinner saving darkTheme />
          </FadingIcon>
          <FadingIcon isVisible={!showSpinner}>
            <IcomoonReact
              iconSet={iconSet}
              size={20}
              icon='cloud-upload'
              color={colors.textSecondary}
            />
          </FadingIcon>
        </IconContainer>
      </BorderedIconWrapper>

      {currentDayLastUpdated && (
        <ReactTooltip
          id={uploadTooltipId}
          place='left'
          content={`Last updated: ${moment(currentDayLastUpdated).format(
            'MMM D, YYYY h:mm A',
          )}`}
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: colors.white,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
          }}
        />
      )}
    </>
  );
};

export default CloudUploadIcon;
